import { Icon, pxToRem, Text } from '@lbcamden/lib-react';
import { formatDMYWithTime } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';

import { Markdown } from '~/client/components/common/Markdown';
import { DirectoryUserInfo } from '~/client/components/shared/DirectoryUserInfo';
import { apiRoutes } from '~/client/config/routes';

import { AnyHistoryItem } from '../types';
import {
  DetailsContainer,
  Divider,
  HistoryItemRoot,
  InlineAttributes,
  NoteAuthor,
  NoteContainer,
} from './HistoryItem.styled';

type Props = {
  propertyId: number;
  item: AnyHistoryItem;
};

const getTitle = (item: AnyHistoryItem) => {
  switch (item.type) {
    case 'note':
      return 'Note added';
    case 'document':
      return `${item.data.documentType} document published`;
    default:
      return 'Activity';
  }
};

const renderContent = ({ propertyId, item }: Props) => {
  switch (item.type) {
    case 'note':
      return (
        <>
          <NoteContainer>
            <Markdown>{item.data.note}</Markdown>
          </NoteContainer>
          <NoteAuthor>
            <DirectoryUserInfo camdenAccountName={item.data.createdBy} />
          </NoteAuthor>
        </>
      );

    case 'document': {
      const { url, title, isActive, documentPropertyId } = item.data;
      const attributes = [
        isActive && (
          <Text key="active" $color="success" $weight={600}>
            Active
          </Text>
        ),
        <Text key="residence">
          {documentPropertyId === propertyId ? 'Current residence' : 'Previous residence'}
        </Text>,
      ].filter(Boolean);

      return (
        <>
          <a href={apiRoutes.getDocument(url)} target="_blank" rel="noreferrer">
            <Icon icon="ui/file-pdf-2" height={pxToRem(16)} />
            {title}
          </a>
          <InlineAttributes>
            {attributes.map((attr, index) => [
              attr,
              index !== attributes.length - 1 && <Divider key={`divider-${index}`} />,
            ])}
          </InlineAttributes>
        </>
      );
    }

    default:
      return null;
  }
};

export const HistoryItem: FunctionComponent<Props> = ({ propertyId, item }) => (
  <HistoryItemRoot data-testid={`history-item-${item.type}-${item.id}`}>
    <DetailsContainer>
      <Text $weight={600}>{getTitle(item)}</Text>
      {renderContent({ propertyId, item })}
    </DetailsContainer>
    <Text>{formatDMYWithTime(item.timestamp)}</Text>
  </HistoryItemRoot>
);
